import React, { useState, useEffect, useMemo } from 'react';
import { fetchDistrictStores, fetchFacilityItems } from '../data/api';

const FACILITY_TYPES_WITH_ITEMS = ['ddwh', 'mc', 'dh', 'sdh', 'chc', 'phc'];

// Map store_type_master type_name to facType key for item stock lookup
const TYPE_NAME_TO_FACTYPE = {
  'DDWH': 'ddwh', 'MC': 'mc', 'DH': 'dh', 'SDH': 'sdh', 'SD': 'sdh',
  'CHC': 'chc', 'SHC': 'chc', 'PHC': 'phc', 'BPHC': 'phc',
  'MPHC': 'phc', 'UPHC': 'phc', 'Riverine PHC': 'phc', 'Model Hospital': 'dh',
};

const FACTYPE_LABELS = {
  ddwh: 'DDWH', mc: 'Medical College', dh: 'District Hospital',
  sdh: 'Sub-Divisional Hospital', chc: 'CHC', phc: 'PHC', other: 'Other', all: 'All Facilities'
};

const statusBadge = (val) => {
  const v = parseFloat(val);
  const bg = v >= 90 ? '#dcfce7' : v >= 75 ? '#fef3c7' : v >= 50 ? '#ffedd5' : '#fee2e2';
  const cl = v >= 90 ? '#15803d' : v >= 75 ? '#b45309' : v >= 50 ? '#c2410c' : '#b91c1c';
  const label = v >= 90 ? 'Good' : v >= 75 ? 'Moderate' : v >= 50 ? 'Low' : 'Critical';
  return (
    <span style={{ background: bg, color: cl, fontWeight: 700, borderRadius: 12, padding: '3px 10px', fontSize: '.7rem', display: 'inline-block' }}>
      {v}% — {label}
    </span>
  );
};

const progressBar = (val, height = 8) => {
  const v = parseFloat(val);
  const color = v >= 90 ? '#27ae60' : v >= 75 ? '#f39c12' : v >= 50 ? '#e67e22' : '#e74c3c';
  return (
    <div style={{ width: '100%', height, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
      <div style={{ width: `${Math.min(v, 100)}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.4s ease' }} />
    </div>
  );
};

const monthsBadge = (val) => {
  const v = parseFloat(val);
  if (v === 0) return <span style={{ color: '#b91c1c', fontWeight: 700, fontSize: '.72rem' }}>0</span>;
  const bg = v >= 3 ? '#dcfce7' : v >= 1 ? '#fef3c7' : '#fee2e2';
  const cl = v >= 3 ? '#15803d' : v >= 1 ? '#b45309' : '#b91c1c';
  return (
    <span style={{ background: bg, color: cl, fontWeight: 700, borderRadius: 12, padding: '2px 8px', fontSize: '.68rem', display: 'inline-block' }}>
      {v}
    </span>
  );
};

const itemStatusBadge = (stock, months) => {
  if (parseInt(stock) === 0) return <span style={{ background: '#fee2e2', color: '#b91c1c', fontWeight: 700, borderRadius: 12, padding: '2px 8px', fontSize: '.65rem' }}>Out</span>;
  if (parseFloat(months) < 1) return <span style={{ background: '#fef3c7', color: '#b45309', fontWeight: 700, borderRadius: 12, padding: '2px 8px', fontSize: '.65rem' }}>Critical</span>;
  if (parseFloat(months) < 3) return <span style={{ background: '#fef3c7', color: '#92400e', fontWeight: 700, borderRadius: 12, padding: '2px 8px', fontSize: '.65rem' }}>Low</span>;
  return <span style={{ background: '#dcfce7', color: '#15803d', fontWeight: 700, borderRadius: 12, padding: '2px 8px', fontSize: '.65rem' }}>OK</span>;
};

export default function DistrictStoreModal({ district, mode, facType, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('avail');
  const [sortDir, setSortDir] = useState('asc');
  const [filterType, setFilterType] = useState('all');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  // For two-level drill-down: null = facility list, storeId = item detail
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [itemData, setItemData] = useState(null);
  const [itemLoading, setItemLoading] = useState(false);

  const hasFacilityItems = FACILITY_TYPES_WITH_ITEMS.includes(facType);

  // Initial data fetch
  useEffect(() => {
    if (!district) return;
    setLoading(true);
    setSelectedFacility(null);
    setItemData(null);

    if (hasFacilityItems) {
      fetchFacilityItems(facType, { district })
        .then(r => { setData(r.data); setLoading(false); })
        .catch(e => { console.error(e); setLoading(false); });
    } else {
      fetchDistrictStores(district)
        .then(r => { setData(r.data); setLoading(false); })
        .catch(e => { console.error(e); setLoading(false); });
    }
  }, [district, facType, hasFacilityItems]);

  // Drill into specific facility
  const handleFacilityClick = (storeId, storeName) => {
    setSelectedFacility({ storeId, name: storeName });
    setItemLoading(true);
    fetchFacilityItems(facType, { storeId })
      .then(r => { setItemData(r.data); setItemLoading(false); })
      .catch(e => { console.error(e); setItemLoading(false); });
  };

  const handleBackToList = () => {
    setSelectedFacility(null);
    setItemData(null);
    setSearch('');
    setFilterType('all');
    setFilterStatus('all');
  };

  // Drill into a store from the "All Facilities" store list
  const handleStoreDetail = (storeId, storeName, facilityType) => {
    const ft = TYPE_NAME_TO_FACTYPE[facilityType] || TYPE_NAME_TO_FACTYPE[facilityType?.toUpperCase()] || 'phc';
    setSelectedFacility({ storeId, name: storeName, facTypeOverride: ft });
    setItemLoading(true);
    setSearch('');
    setFilterType('all');
    fetchFacilityItems(ft, { storeId })
      .then(r => { setItemData(r.data); setItemLoading(false); })
      .catch(e => { console.error(e); setItemLoading(false); });
  };

  // Facility types for store-level filter
  const facilityTypes = useMemo(() => {
    if (hasFacilityItems || !data?.stores) return [];
    return [...new Set(data.stores.map(s => s.facility_type))].filter(t => t !== '-').sort();
  }, [data, hasFacilityItems]);

  // Item categories for item-level filter
  const itemCategories = useMemo(() => {
    const items = selectedFacility ? itemData?.items : data?.items;
    if (!items) return [];
    return [...new Set(items.map(i => i.category))].sort();
  }, [data, itemData, selectedFacility]);

  // Filtered items/stores
  const filteredItems = useMemo(() => {
    // Item detail view (for a specific facility or DDWH district, or store drill-down from All Facilities)
    if ((hasFacilityItems && (selectedFacility || facType === 'ddwh')) || (!hasFacilityItems && selectedFacility)) {
      const items = selectedFacility ? itemData?.items : data?.items;
      if (!items) return [];
      let filtered = [...items];
      if (filterType !== 'all') filtered = filtered.filter(i => i.category === filterType);
      if (search) filtered = filtered.filter(i => i.name && i.name.toLowerCase().includes(search.toLowerCase()));
      if (filterStatus !== 'all') {
        filtered = filtered.filter(i => {
          const stock = parseInt(i.stock) || 0;
          const months = parseFloat(i.months_stock) || 0;
          if (filterStatus === 'out') return stock === 0;
          if (filterStatus === 'critical') return stock > 0 && months < 1;
          if (filterStatus === 'low') return months >= 1 && months < 3;
          if (filterStatus === 'ok') return months >= 3;
          return true;
        });
      }
      filtered.sort((a, b) => {
        let aV, bV;
        if (sortField === 'stock') { aV = parseInt(a.stock) || 0; bV = parseInt(b.stock) || 0; }
        else if (sortField === 'months') { aV = parseFloat(a.months_stock) || 0; bV = parseFloat(b.months_stock) || 0; }
        else { aV = a.name || ''; bV = b.name || ''; }
        return sortDir === 'asc' ? (aV > bV ? 1 : -1) : (aV < bV ? 1 : -1);
      });
      return filtered;
    }

    // Store-level view (All Facilities)
    if (!hasFacilityItems && !selectedFacility && data?.stores) {
      let stores = [...data.stores];
      if (filterType !== 'all') stores = stores.filter(s => s.facility_type === filterType);
      if (search) stores = stores.filter(s => s.name.toLowerCase().includes(search.toLowerCase()));
      if (filterStatus !== 'all') {
        stores = stores.filter(s => {
          const v = parseFloat(s.avail);
          if (filterStatus === 'good') return v >= 90;
          if (filterStatus === 'warning') return v >= 75 && v < 90;
          if (filterStatus === 'low') return v >= 50 && v < 75;
          if (filterStatus === 'critical') return v < 50;
          return true;
        });
      }
      stores.sort((a, b) => {
        const aV = sortField === 'avail' ? parseFloat(a.avail) : a.name;
        const bV = sortField === 'avail' ? parseFloat(b.avail) : b.name;
        return sortDir === 'asc' ? (aV > bV ? 1 : -1) : (aV < bV ? 1 : -1);
      });
      return stores;
    }

    return [];
  }, [data, itemData, filterType, filterStatus, search, sortField, sortDir, hasFacilityItems, selectedFacility, facType]);

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const summary = selectedFacility ? itemData?.summary : data?.summary;
  const facilities = data?.facilities || [];

  // Export to CSV / Excel
  const handleExport = (format) => {
    if (!filteredItems.length) return;
    const title = selectedFacility ? selectedFacility.name : district;
    let headers, rows;

    if (showItemDetail || (!hasFacilityItems && selectedFacility)) {
      headers = ['#', 'Item Name', 'Category', 'Stock Qty', 'AMC', 'Months', 'Status'];
      rows = filteredItems.map((item, i) => {
        const stock = parseInt(item.stock) || 0;
        const months = parseFloat(item.months_stock) || 0;
        const status = stock === 0 ? 'Out of Stock' : months < 1 ? 'Critical' : months < 3 ? 'Low' : 'OK';
        return [i + 1, item.name, item.category || '', stock, parseFloat(item.amc) > 0 ? Math.round(item.amc) : '', months, status];
      });
    } else {
      headers = ['#', 'Store Name', 'Facility Type', 'Availability %', 'Status'];
      rows = filteredItems.map((s, i) => {
        const v = parseFloat(s.avail);
        const status = v >= 90 ? 'Good' : v >= 75 ? 'Moderate' : v >= 50 ? 'Low' : 'Critical';
        return [i + 1, s.name, s.facility_type || '', v, status];
      });
    }

    // Build CSV content
    const escapeCSV = (val) => {
      const str = String(val ?? '');
      return str.includes(',') || str.includes('"') || str.includes('\n') ? `"${str.replace(/"/g, '""')}"` : str;
    };
    const csvContent = [headers.join(','), ...rows.map(r => r.map(escapeCSV).join(','))].join('\n');
    const BOM = '\uFEFF';

    if (format === 'csv') {
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.replace(/[^a-zA-Z0-9]/g, '_')}_stock.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      // Excel XML format
      const xmlRows = rows.map(r =>
        '<Row>' + r.map((c, ci) => {
          const type = typeof c === 'number' ? 'Number' : 'String';
          return `<Cell><Data ss:Type="${type}">${String(c ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;')}</Data></Cell>`;
        }).join('') + '</Row>'
      ).join('\n');
      const xml = `<?xml version="1.0"?><?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
<Worksheet ss:Name="Stock Data"><Table>
<Row>${headers.map(h => `<Cell><Data ss:Type="String">${h}</Data></Cell>`).join('')}</Row>
${xmlRows}
</Table></Worksheet></Workbook>`;
      const blob = new Blob([xml], { type: 'application/vnd.ms-excel' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.replace(/[^a-zA-Z0-9]/g, '_')}_stock.xls`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  // Determine view mode
  const showFacilityList = hasFacilityItems && !selectedFacility && facType !== 'ddwh' && facilities.length > 0;
  const showItemDetail = (hasFacilityItems && (selectedFacility || facType === 'ddwh')) || (!hasFacilityItems && selectedFacility);
  if (!district) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)',
      zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'fadeIn 0.2s ease'
    }}>
      <div style={{
        background: '#fff', borderRadius: 16, width: '93%', maxWidth: 1050,
        maxHeight: '88vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 25px 50px rgba(0,0,0,0.25)', animation: 'slideUp 0.3s ease'
      }} onClick={e => e.stopPropagation()}>

        {/* ===== HEADER ===== */}
        <div style={{
          padding: '20px 24px 16px', borderBottom: '1px solid #e2e8f0',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)',
          borderRadius: '16px 16px 0 0', color: '#fff'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              {/* Breadcrumb for drill-down */}
              {selectedFacility && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                  <button onClick={handleBackToList} style={{
                    background: 'rgba(255,255,255,0.15)', border: 'none', color: '#93c5fd',
                    padding: '4px 12px', borderRadius: 6, cursor: 'pointer', fontSize: '.72rem',
                    fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4
                  }}>
                    <i className="fas fa-arrow-left" style={{ fontSize: '.65rem' }}></i>
                    Back to {hasFacilityItems ? FACTYPE_LABELS[facType] : 'Store'} List
                  </button>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800 }}>
                  {selectedFacility ? selectedFacility.name : district}
                </h2>
                {facType && facType !== 'all' && (
                  <span style={{
                    background: 'rgba(255,255,255,0.2)', padding: '3px 12px',
                    borderRadius: 12, fontSize: '.7rem', fontWeight: 600
                  }}>
                    {FACTYPE_LABELS[facType] || facType.toUpperCase()}
                  </span>
                )}
              </div>
              <p style={{ margin: '4px 0 0', fontSize: '.78rem', opacity: 0.8 }}>
                {showFacilityList ? `${FACTYPE_LABELS[facType]} Facilities in ${district}` :
                 showItemDetail ? 'Drug Stock — Item Level Detail' :
                 'Stock Availability — Store Level Detail'}
              </p>
            </div>
            <button onClick={onClose} style={{
              background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff',
              width: 36, height: 36, borderRadius: 8, cursor: 'pointer',
              fontSize: '1.1rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>✕</button>
          </div>

          {/* Summary Stats */}
          {summary && !loading && (
            <div style={{ display: 'flex', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
              {(showItemDetail || (showFacilityList && summary)) ? (
                [
                  { label: showFacilityList ? 'Facilities' : 'Total Items', value: showFacilityList ? facilities.length : summary.total_items, color: '#93c5fd' },
                  { label: 'Availability', value: `${summary.avl_pct}%`, color: parseFloat(summary.avl_pct) >= 90 ? '#86efac' : parseFloat(summary.avl_pct) >= 75 ? '#fde68a' : '#fca5a5' },
                  { label: 'In Stock', value: summary.in_stock, color: '#86efac' },
                  { label: 'Out of Stock', value: summary.out_of_stock, color: '#fca5a5' },
                  { label: 'Critical (<1 mo)', value: summary.critical, color: '#fca5a5' },
                  { label: 'Low (1-3 mo)', value: summary.low, color: '#fde68a' },
                ].map((s, i) => (
                  <div key={i} style={{
                    background: 'rgba(255,255,255,0.12)', borderRadius: 8, padding: '8px 12px',
                    minWidth: 70, textAlign: 'center', flex: 1
                  }}>
                    <div style={{ fontSize: '1rem', fontWeight: 800, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: '.62rem', opacity: 0.7 }}>{s.label}</div>
                  </div>
                ))
              ) : (
                [
                  { label: 'Total Stores', value: summary.total_stores, color: '#93c5fd' },
                  { label: 'Avg Availability', value: `${summary.avg_avail}%`, color: summary.avg_avail >= 90 ? '#86efac' : summary.avg_avail >= 75 ? '#fde68a' : '#fca5a5' },
                  { label: '\u2265 90%', value: summary.above_90, color: '#86efac' },
                  { label: '75-90%', value: summary.pct_75_90, color: '#fde68a' },
                  { label: '50-75%', value: summary.pct_50_75, color: '#fdba74' },
                  { label: '< 50%', value: summary.below_50, color: '#fca5a5' },
                ].map((s, i) => (
                  <div key={i} style={{
                    background: 'rgba(255,255,255,0.12)', borderRadius: 8, padding: '8px 12px',
                    minWidth: 70, textAlign: 'center', flex: 1
                  }}>
                    <div style={{ fontSize: '1rem', fontWeight: 800, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: '.62rem', opacity: 0.7 }}>{s.label}</div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* ===== FILTER BAR ===== */}
        {!showFacilityList && (
          <div style={{ padding: '12px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder={showItemDetail ? "Search item name..." : "Search store name..."}
              value={search} onChange={e => setSearch(e.target.value)}
              style={{
                padding: '8px 14px', border: '1px solid #e2e8f0', borderRadius: 8,
                fontSize: '.8rem', flex: 1, minWidth: 180, outline: 'none', background: '#f8fafc'
              }}
            />
            <select
              value={filterType} onChange={e => setFilterType(e.target.value)}
              style={{
                padding: '8px 14px', border: '1px solid #e2e8f0', borderRadius: 8,
                fontSize: '.8rem', background: '#f8fafc', cursor: 'pointer', outline: 'none'
              }}
            >
              <option value="all">{showItemDetail ? 'All Categories' : 'All Facility Types'}</option>
              {showItemDetail
                ? itemCategories.map(c => <option key={c} value={c}>{c}</option>)
                : facilityTypes.map(t => <option key={t} value={t}>{t}</option>)
              }
            </select>
            <select
              value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              style={{
                padding: '8px 14px', border: '1px solid #e2e8f0', borderRadius: 8,
                fontSize: '.8rem', background: '#f8fafc', cursor: 'pointer', outline: 'none'
              }}
            >
              {showItemDetail ? (
                <>
                  <option value="all">All Status</option>
                  <option value="ok">✅ OK (≥3 mo)</option>
                  <option value="low">⚠️ Low (1-3 mo)</option>
                  <option value="critical">🔶 Critical (&lt;1 mo)</option>
                  <option value="out">🔴 Out of Stock</option>
                </>
              ) : (
                <>
                  <option value="all">All Status</option>
                  <option value="good">✅ Good (≥90%)</option>
                  <option value="warning">⚠️ Moderate (75-89%)</option>
                  <option value="low">🔶 Low (50-74%)</option>
                  <option value="critical">🔴 Critical (&lt;50%)</option>
                </>
              )}
            </select>
            <span style={{ fontSize: '.75rem', color: '#64748b', fontWeight: 600 }}>
              {filteredItems.length} {showItemDetail ? 'item' : 'store'}{filteredItems.length !== 1 ? 's' : ''}
            </span>
            <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
              <button onClick={() => handleExport('csv')} style={{
                padding: '6px 12px', borderRadius: 6, border: '1px solid #10b981',
                background: '#ecfdf5', color: '#059669', fontSize: '.72rem',
                fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4
              }}
              onMouseOver={e => { e.currentTarget.style.background = '#059669'; e.currentTarget.style.color = '#fff'; }}
              onMouseOut={e => { e.currentTarget.style.background = '#ecfdf5'; e.currentTarget.style.color = '#059669'; }}
              >
                <i className="fas fa-file-csv" style={{ fontSize: '.7rem' }}></i> CSV
              </button>
              <button onClick={() => handleExport('excel')} style={{
                padding: '6px 12px', borderRadius: 6, border: '1px solid #2563eb',
                background: '#eff6ff', color: '#2563eb', fontSize: '.72rem',
                fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4
              }}
              onMouseOver={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.color = '#fff'; }}
              onMouseOut={e => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#2563eb'; }}
              >
                <i className="fas fa-file-excel" style={{ fontSize: '.7rem' }}></i> Excel
              </button>
            </div>
          </div>
        )}

        {/* ===== CONTENT ===== */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {(loading || itemLoading) ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
              <div style={{ fontSize: '2rem', marginBottom: 10 }}>
                <i className="fas fa-spinner fa-spin" style={{ color: '#3b82f6' }}></i>
              </div>
              Loading data...
            </div>
          ) : showFacilityList ? (
            /* ===== FACILITY LIST (MC/DH/SDH in district) ===== */
            <div style={{ padding: '0' }}>
              <div style={{ padding: '12px 24px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <span style={{ fontSize: '.8rem', fontWeight: 700, color: '#1e293b' }}>
                  <i className="fas fa-hospital" style={{ color: '#3b82f6', marginRight: 8 }}></i>
                  {facilities.length} {FACTYPE_LABELS[facType]} Facilit{facilities.length !== 1 ? 'ies' : 'y'} in {district}
                </span>
                <span style={{ fontSize: '.72rem', color: '#64748b', marginLeft: 12 }}>
                  Click any facility to view drug-level stock detail
                </span>
              </div>
              {facilities.map((f, i) => {
                const avail = parseFloat(f.avail);
                return (
                  <div key={i}
                    onClick={() => handleFacilityClick(f.store_id, f.name)}
                    style={{
                      padding: '16px 24px', borderBottom: '1px solid #f1f5f9',
                      cursor: 'pointer', transition: 'all 0.2s',
                      background: i % 2 === 0 ? '#fff' : '#fafbfc',
                      display: 'flex', alignItems: 'center', gap: 20
                    }}
                    onMouseOver={e => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.transform = 'translateX(4px)'; }}
                    onMouseOut={e => { e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fafbfc'; e.currentTarget.style.transform = 'none'; }}
                  >
                    {/* Facility Icon */}
                    <div style={{
                      width: 48, height: 48, borderRadius: 12,
                      background: avail >= 90 ? '#dcfce7' : avail >= 75 ? '#fef3c7' : '#fee2e2',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                    }}>
                      <i className={facType === 'mc' ? 'fas fa-university' : facType === 'dh' ? 'fas fa-hospital' : facType === 'chc' ? 'fas fa-house-medical' : facType === 'phc' ? 'fas fa-briefcase-medical' : 'fas fa-clinic-medical'}
                        style={{ fontSize: '1.2rem', color: avail >= 90 ? '#15803d' : avail >= 75 ? '#b45309' : '#b91c1c' }}></i>
                    </div>

                    {/* Facility Info */}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: '.88rem', color: '#1e293b', marginBottom: 4 }}>{f.name}</div>
                      <div style={{ display: 'flex', gap: 16, fontSize: '.72rem', color: '#64748b' }}>
                        <span><b>{f.total_items}</b> items</span>
                        <span style={{ color: '#27ae60' }}><b>{f.items_with_stock}</b> in stock</span>
                        <span style={{ color: '#e74c3c' }}><b>{f.items_zero_stock}</b> out of stock</span>
                      </div>
                    </div>

                    {/* Availability */}
                    <div style={{ width: 120, textAlign: 'center' }}>
                      {progressBar(avail, 10)}
                      <div style={{ marginTop: 4 }}>{statusBadge(avail)}</div>
                    </div>

                    {/* Arrow */}
                    <div style={{ color: '#3b82f6', fontSize: '1rem' }}>
                      <i className="fas fa-chevron-right"></i>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : showItemDetail ? (
            /* ===== ITEM DETAIL TABLE ===== */
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.78rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc', position: 'sticky', top: 0, zIndex: 2 }}>
                  <th style={thStyle}>#</th>
                  <th style={{ ...thStyle, textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('name')}>
                    Item Name {sortField === 'name' ? (sortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
                  </th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Category</th>
                  <th style={{ ...thStyle, textAlign: 'right', cursor: 'pointer' }} onClick={() => toggleSort('stock')}>
                    Stock Qty {sortField === 'stock' ? (sortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
                  </th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>AMC</th>
                  <th style={{ ...thStyle, textAlign: 'center', cursor: 'pointer' }} onClick={() => toggleSort('months')}>
                    Months {sortField === 'months' ? (sortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
                  </th>
                  <th style={{ ...thStyle, textAlign: 'center', width: 70 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item, i) => {
                  const stockVal = parseInt(item.stock) || 0;
                  const isZero = stockVal === 0;
                  const months = parseFloat(item.months_stock) || 0;
                  return (
                    <tr key={i} style={{
                      background: isZero ? '#fff5f5' : months > 0 && months < 1 ? '#fffbeb' : i % 2 === 0 ? '#fff' : '#fafbfc',
                      transition: 'background 0.15s'
                    }}
                    onMouseOver={e => e.currentTarget.style.background = '#f0f7ff'}
                    onMouseOut={e => e.currentTarget.style.background = isZero ? '#fff5f5' : months > 0 && months < 1 ? '#fffbeb' : i % 2 === 0 ? '#fff' : '#fafbfc'}
                    >
                      <td style={{ ...tdStyle, color: '#94a3b8', fontWeight: 600, width: 36, textAlign: 'center' }}>{i + 1}</td>
                      <td style={{ ...tdStyle, fontWeight: 600, maxWidth: 280 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.name}>
                          {item.name}
                        </div>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        <span style={{ background: '#f1f5f9', padding: '2px 8px', borderRadius: 12, fontSize: '.66rem', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>
                          {item.category}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: isZero ? '#b91c1c' : '#1e293b' }}>
                        {stockVal.toLocaleString()}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right', color: '#64748b' }}>
                        {parseFloat(item.amc) > 0 ? Math.round(item.amc).toLocaleString() : '-'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{monthsBadge(item.months_stock)}</td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>{itemStatusBadge(item.stock, item.months_stock)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : filteredItems.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>
              <div style={{ fontSize: '2rem', marginBottom: 10 }}>
                <i className="fas fa-inbox" style={{ color: '#d1d5db' }}></i>
              </div>
              No stores found for this filter
            </div>
          ) : (
            /* ===== STORE LIST TABLE (CHC, PHC, etc.) ===== */
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.78rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc', position: 'sticky', top: 0, zIndex: 2 }}>
                  <th style={thStyle}>#</th>
                  <th style={{ ...thStyle, textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('name')}>
                    Store Name {sortField === 'name' ? (sortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
                  </th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Facility Type</th>
                  <th style={{ ...thStyle, textAlign: 'center', width: 140 }}>Availability</th>
                  <th style={{ ...thStyle, textAlign: 'center', cursor: 'pointer', width: 120 }} onClick={() => toggleSort('avail')}>
                    Status {sortField === 'avail' ? (sortDir === 'asc' ? '\u25B2' : '\u25BC') : ''}
                  </th>
                  <th style={{ ...thStyle, textAlign: 'center', width: 100 }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((s, i) => (
                  <tr key={i} style={{
                    background: parseFloat(s.avail) < 50 ? '#fff5f5' : i % 2 === 0 ? '#fff' : '#fafbfc',
                    transition: 'background 0.15s'
                  }}
                  onMouseOver={e => e.currentTarget.style.background = '#f0f7ff'}
                  onMouseOut={e => e.currentTarget.style.background = parseFloat(s.avail) < 50 ? '#fff5f5' : i % 2 === 0 ? '#fff' : '#fafbfc'}
                  >
                    <td style={{ ...tdStyle, color: '#94a3b8', fontWeight: 600, width: 40, textAlign: 'center' }}>{i + 1}</td>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{s.name}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <span style={{ background: '#f1f5f9', padding: '2px 10px', borderRadius: 12, fontSize: '.68rem', fontWeight: 600, color: '#475569' }}>{s.facility_type}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center', width: 140 }}>{progressBar(s.avail)}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>{statusBadge(s.avail)}</td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <button onClick={() => handleStoreDetail(s.store_id, s.name, s.facility_type)} style={{
                        padding: '4px 12px', borderRadius: 6, border: '1px solid #3b82f6',
                        background: '#eff6ff', color: '#2563eb', fontSize: '.68rem',
                        fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s',
                        whiteSpace: 'nowrap'
                      }}
                      onMouseOver={e => { e.currentTarget.style.background = '#3b82f6'; e.currentTarget.style.color = '#fff'; }}
                      onMouseOut={e => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.color = '#2563eb'; }}
                      >
                        <i className="fas fa-eye" style={{ marginRight: 4, fontSize: '.6rem' }}></i>
                        View Detail
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(30px) } to { opacity: 1; transform: translateY(0) } }
      `}</style>
    </div>
  );
}

const thStyle = {
  padding: '10px 14px', fontWeight: 700, fontSize: '.7rem',
  textTransform: 'uppercase', letterSpacing: '.5px', color: '#64748b',
  borderBottom: '2px solid #e2e8f0', whiteSpace: 'nowrap'
};

const tdStyle = {
  padding: '10px 14px', borderBottom: '1px solid #f1f5f9'
};
